import { createSlice } from "@reduxjs/toolkit";
/**
 * How many requests a list remembers.
 *
 * Search is debounced, so typing still writes an entry per pause, and an
 * unbounded map would hold every query of the session with its rows. Six covers
 * the unsearched list plus a few recent queries.
 */
const MAX_ENTRIES = 6;
const initialState = { byKey: {} };
/** Moves a params key to the front, dropping whatever falls off the end. */
const touch = (bucket, paramsKey) => {
    bucket.order = [paramsKey, ...bucket.order.filter((k) => k !== paramsKey)];
    for (const evicted of bucket.order.splice(MAX_ENTRIES)) {
        delete bucket.entries[evicted];
    }
};
/**
 * The last rows each player list showed.
 *
 * Lists used to keep their rows in component state, so every remount — a tab
 * switch, a drawer navigation — rendered an empty list and waited a second for
 * the response. Cached here, the same rows come back instantly and the fetch
 * that follows only replaces them once it lands.
 */
const cachedPlayerListsSlice = createSlice({
    name: "cachedPlayerLists",
    initialState,
    reducers: {
        cachedPlayerListLoaded: (state, action) => {
            const { key, paramsKey, players, totalPages } = action.payload;
            const bucket = (state.byKey[key] ??= { order: [], entries: {} });
            bucket.entries[paramsKey] = { paramsKey, players, page: 1, totalPages };
            touch(bucket, paramsKey);
        },
        cachedPlayerListAppended: (state, action) => {
            const { key, paramsKey, players, page, totalPages } = action.payload;
            const cached = state.byKey[key]?.entries[paramsKey];
            // A page that arrives out of order, or for a request whose rows have
            // since been evicted, is stale.
            if (!cached || page <= cached.page)
                return;
            // Appended whole: trimming the front would pull rows out from under a
            // list the user is scrolling. The next page-1 load replaces the lot.
            cached.players.push(...players);
            cached.page = page;
            cached.totalPages = totalPages;
        },
    },
});
export const { cachedPlayerListLoaded, cachedPlayerListAppended } = cachedPlayerListsSlice.actions;
/**
 * The rows a list last showed for one request, or an empty list.
 *
 * Returns the shared `EMPTY_CACHED_PLAYER_LIST` so an untouched list keeps the same
 * reference across renders — callers pass these straight into memo deps.
 */
export const EMPTY_CACHED_PLAYER_LIST = {
    paramsKey: "",
    players: [],
    page: 1,
    totalPages: 0,
};
export const cachedPlayerListSelector = (key, paramsKey) => (state) => state.cachedPlayerListsReducer.byKey[key]?.entries[paramsKey] ??
    EMPTY_CACHED_PLAYER_LIST;
export default cachedPlayerListsSlice.reducer;
//# sourceMappingURL=cachedPlayerListsSlice.js.map